import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Edit, Plus, Eye, EyeOff } from 'lucide-react';
import { getDirectUrl } from '../../utils/mediaUtils';
import { GenZLessonModal } from './GenZLessonModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Badge = ({ ok, label, testId }) => (
  <span
    className={`text-xs font-medium rounded px-2 py-0.5 border ${ok ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-slate-400 bg-slate-50 border-slate-200'}`}
    data-testid={testId}
  >
    {ok ? label : `No ${label}`}
  </span>
);

export const GenZManager = () => {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const fetchLessons = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/genz/lessons`, { withCredentials: true });
      setLessons(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLessons(); }, []);

  return (
    <div data-testid="genz-manager">
      <div className="flex justify-between items-start mb-6 gap-4">
        <div>
          <h2 className="font-heading text-2xl font-medium text-brand-primary mb-1">Gen Z: God’s Emerging Warriors</h2>
          <p className="font-body text-sm text-slate-600">Upload or replace each lesson's thumbnail, video, MP3 and PDF. Only published lessons appear on the website.</p>
        </div>
        <button
          onClick={() => setEditing({})}
          className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-brand-accent text-white font-body font-medium rounded hover:bg-brand-accent-hover transition-colors duration-300"
          data-testid="add-genz-lesson-button"
        >
          <Plus className="h-4 w-4" /> Add Lesson
        </button>
      </div>
      {loading ? (
        <p className="font-body text-slate-600">Loading...</p>
      ) : (
        <div className="space-y-3" data-testid="admin-genz-list">
          {lessons.map((l) => (
            <div key={l.id} className="bg-white border border-slate-200 rounded shadow-sm p-4 flex items-center gap-4" data-testid={`admin-genz-row-${l.order}`}>
              <div className="w-24 h-16 flex-shrink-0 bg-slate-100 rounded overflow-hidden">
                {l.thumbnail_url && <img src={getDirectUrl(l.thumbnail_url)} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-grow min-w-0">
                <p className="text-xs font-semibold text-brand-accent tracking-widest flex items-center gap-2">
                  LESSON {l.order}
                  {l.published
                    ? <span className="inline-flex items-center gap-1 text-emerald-700"><Eye className="h-3 w-3" /> Published</span>
                    : <span className="inline-flex items-center gap-1 text-slate-400"><EyeOff className="h-3 w-3" /> Hidden</span>}
                </p>
                <p className="font-heading text-base font-medium text-brand-primary truncate">{l.title}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Badge ok={!!l.video_url} label="Video" testId={`admin-genz-video-status-${l.order}`} />
                  <Badge ok={!!l.audio_url} label="MP3" testId={`admin-genz-audio-status-${l.order}`} />
                  <Badge ok={!!l.pdf_url} label="PDF" testId={`admin-genz-pdf-status-${l.order}`} />
                </div>
              </div>
              <button
                onClick={() => setEditing(l)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-body font-medium text-brand-primary border border-slate-300 rounded hover:border-brand-accent hover:text-brand-accent transition-colors duration-300"
                data-testid={`edit-genz-lesson-${l.order}`}
              >
                <Edit className="h-4 w-4" /> Edit
              </button>
            </div>
          ))}
        </div>
      )}
      {editing && (
        <GenZLessonModal
          lesson={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchLessons(); }}
        />
      )}
    </div>
  );
};
